const NAMESPACE = 'playerv'
const LS_PREFIX = 'playerv.views.'
const SS_HIT_PREFIX = 'playerv.hit.'

/** Free public counter (CountAPI-compatible), no account needed */
const COUNTER_BASE = 'https://abacus.jasoncameron.dev'

function sanitizeKey(key: string): string {
  return (
    key
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 64) || 'video'
  )
}

/** Stable short hash for auto keys from a video URL/title */
export function hashKey(input: string): string {
  let h = 2166136261
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return `v${(h >>> 0).toString(36)}`
}

function cacheGet(key: string): number | null {
  try {
    const raw = localStorage.getItem(LS_PREFIX + key)
    if (raw == null) return null
    const n = Number(raw)
    return Number.isFinite(n) ? n : null
  } catch {
    return null
  }
}

function cacheSet(key: string, value: number) {
  try {
    localStorage.setItem(LS_PREFIX + key, String(value))
  } catch {
    /* private mode */
  }
}

function alreadyHitThisSession(key: string): boolean {
  try {
    return sessionStorage.getItem(SS_HIT_PREFIX + key) === '1'
  } catch {
    return false
  }
}

function markHitThisSession(key: string) {
  try {
    sessionStorage.setItem(SS_HIT_PREFIX + key, '1')
  } catch {
    /* ignore */
  }
}

async function remoteGet(key: string): Promise<number | null> {
  const res = await fetch(`${COUNTER_BASE}/get/${NAMESPACE}/${encodeURIComponent(key)}`)
  if (!res.ok) return null
  const data = (await res.json()) as { value?: number }
  return typeof data.value === 'number' ? data.value : null
}

async function remoteHit(key: string): Promise<number | null> {
  const res = await fetch(`${COUNTER_BASE}/hit/${NAMESPACE}/${encodeURIComponent(key)}`)
  if (!res.ok) return null
  const data = (await res.json()) as { value?: number }
  return typeof data.value === 'number' ? data.value : null
}

/**
 * Built-in global view counter — no backend of yours.
 * Uses a public counter API + localStorage cache.
 * One increment per browser tab session.
 */
export const BuiltInViews = {
  sanitizeKey,

  resolveKey(explicit: string | undefined, fingerprint: string | undefined): string | null {
    if (explicit) return sanitizeKey(explicit)
    if (fingerprint) return hashKey(fingerprint)
    return null
  },

  peek(key: string): number | null {
    return cacheGet(sanitizeKey(key))
  },

  async load(key: string): Promise<number> {
    const k = sanitizeKey(key)
    const cached = cacheGet(k) ?? 0
    try {
      const remote = await remoteGet(k)
      if (remote != null) {
        cacheSet(k, remote)
        return remote
      }
    } catch {
      /* offline → local cache */
    }
    return cached
  },

  async hit(key: string): Promise<number> {
    const k = sanitizeKey(key)
    if (alreadyHitThisSession(k)) {
      return (await this.load(k)) || cacheGet(k) || 0
    }
    markHitThisSession(k)

    const cached = cacheGet(k) ?? 0
    const optimistic = cached + 1
    cacheSet(k, optimistic)

    try {
      const remote = await remoteHit(k)
      if (remote != null) {
        cacheSet(k, remote)
        return remote
      }
    } catch {
      /* keep optimistic local count */
    }
    return optimistic
  },
}
